/* Phase B1 canvas editor — object-based rendering, selection, direct text edit.
   Renders `book_pages.objects` on top of each page card. Primary-chapter-body
   objects reflect canonical `book_chapters.content` and sync back to it via
   PATCH /pages/{page_id} { primary_content: ... }. */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../../lib/apiClient";
import { CheckCircle2, Loader2, AlertTriangle, Type, MousePointer2, Plus } from "lucide-react";

const PPI = 96;                    // must match CanvasPagePreview
const PAGE_W_IN = 6;               // 6×9 default trim
const PAGE_H_IN = 9;

/* ---------------------------------------------------------------- Hook */
export function useBookPages(bookId) {
  const [pages, setPages] = useState([]);
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    if (!bookId) return;
    setErr(null);
    try {
      const r = await api.get(`/api/book-lab/books/${bookId}/pages`);
      setPages(r.items || []);
      setReady(true);
    } catch (e) { setErr(e.message || String(e)); }
  }, [bookId]);
  useEffect(() => { load(); }, [load]);

  // Optimistic page-object update — reflected in UI immediately.
  const applyOptimistic = useCallback((pageId, nextObjects) => {
    setPages((prev) => prev.map((p) => (p.id === pageId ? { ...p, objects: nextObjects } : p)));
  }, []);

  const patchPage = useCallback(async (pageId, next, opts = {}) => {
    const r = await api.patch(`/api/book-lab/books/${bookId}/pages/${pageId}`, {
      objects: next.objects,
      revision: next.revision,
      primary_content: opts.primary_content,
    });
    setPages((prev) => prev.map((p) => (p.id === pageId ? r : p)));
    return r;
  }, [bookId]);

  return { pages, ready, err, reload: load, patchPage, applyOptimistic };
}

/* ---------------------------------------------------------------- Save banner */
export function SaveStatusBanner({ status, error, onRetry }) {
  const map = {
    idle:     { text: "All changes saved", icon: CheckCircle2, color: "#86EFAC" },
    saving:   { text: "Saving…",          icon: Loader2,      color: "#93C5FD", spin: true },
    saved:    { text: "Saved",             icon: CheckCircle2, color: "#86EFAC" },
    failed:   { text: "Save failed",       icon: AlertTriangle, color: "#FCA5A5" },
  };
  const p = map[status] || map.idle;
  const I = p.icon;
  return (
    <div data-testid="canvas-save-status"
         className="fixed bottom-16 left-1/2 -translate-x-1/2 z-40 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px]"
         style={{ background: "rgba(11,27,27,0.9)", color: p.color, border: `1px solid ${p.color}55` }}>
      <I className={"h-3.5 w-3.5 " + (p.spin ? "animate-spin" : "")} />
      {p.text}
      {status === "failed" && error && (
        <>
          <span className="opacity-70">· {error}</span>
          <button data-testid="canvas-save-retry" onClick={onRetry}
                  className="ml-1 px-2 py-0.5 rounded border border-white/20 text-white/90 hover:border-white/40">
            Retry
          </button>
        </>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- Overlay */
export function PageObjectsOverlay({ page, zoom, selectedId, onSelect, activeTool,
                                     onCommitText, onCreateFrame, editingId, setEditingId }) {
  const scale = (zoom || 100) / 100;
  // Convert page-native coords (72 DPI-ish) to on-canvas pixels
  // Page card at 100% zoom = 6*PPI (576px) x 9*PPI (864px) but Canvas actually
  // uses `wIn * PPI * scale` — page-native coords are stored in that same 96-DPI space.
  const w = PAGE_W_IN * PPI * scale;
  const h = PAGE_H_IN * PPI * scale;

  const [creating, setCreating] = useState(null);   // {x0,y0,x,y}

  const onOverlayMouseDown = (e) => {
    if (activeTool !== "text") return;
    e.preventDefault(); e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;
    setCreating({ x0: x, y0: y, x, y });
  };
  const onOverlayMouseMove = (e) => {
    if (!creating) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setCreating((c) => ({ ...c, x: (e.clientX - rect.left) / scale, y: (e.clientY - rect.top) / scale }));
  };
  const onOverlayMouseUp = () => {
    if (!creating) return;
    const x = Math.min(creating.x0, creating.x);
    const y = Math.min(creating.y0, creating.y);
    const w2 = Math.max(80, Math.abs(creating.x - creating.x0));
    const h2 = Math.max(28, Math.abs(creating.y - creating.y0));
    setCreating(null);
    onCreateFrame?.({ x, y, width: w2, height: h2 });
  };

  return (
    <div
      data-testid={`page-overlay-${page.order}`}
      onMouseDown={onOverlayMouseDown}
      onMouseMove={onOverlayMouseMove}
      onMouseUp={onOverlayMouseUp}
      onMouseLeave={() => setCreating(null)}
      className="absolute inset-0"
      style={{
        pointerEvents: activeTool === "text" ? "auto" : "none",
        cursor: activeTool === "text" ? "crosshair" : "default",
      }}
    >
      {(page.objects || []).map((o) => (
        <ObjectView
          key={o.id}
          obj={o}
          scale={scale}
          selected={selectedId === o.id}
          editing={editingId === o.id}
          onSelect={onSelect}
          onCommitText={onCommitText}
          setEditingId={setEditingId}
          activeTool={activeTool}
        />
      ))}
      {creating && (
        <div className="absolute border-2 border-dashed border-[var(--pl-aqua)]"
             style={{
               left: Math.min(creating.x0, creating.x) * scale,
               top: Math.min(creating.y0, creating.y) * scale,
               width: Math.abs(creating.x - creating.x0) * scale,
               height: Math.abs(creating.y - creating.y0) * scale,
               background: "rgba(39,230,209,0.08)",
               pointerEvents: "none",
             }} />
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- Object */
function ObjectView({ obj, scale, selected, editing, onSelect, onCommitText, setEditingId, activeTool }) {
  const ref = useRef(null);
  const [draft, setDraft] = useState(obj.content || "");

  useEffect(() => { setDraft(obj.content || ""); }, [obj.content, obj.id]);

  useEffect(() => {
    if (editing && ref.current) {
      ref.current.focus();
      const range = document.createRange();
      // "New text" placeholder → select all so typing replaces it. Otherwise put caret at end.
      if ((obj.content || "").trim() === "New text") {
        range.selectNodeContents(ref.current);
      } else {
        range.selectNodeContents(ref.current); range.collapse(false);
      }
      const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(range);
    }
  }, [editing, obj.content]);

  const commit = () => {
    const next = ref.current?.innerText ?? draft;
    setEditingId(null);
    if (next !== obj.content) onCommitText?.(obj, next);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") { e.preventDefault(); commit(); }
    // Prevent Backspace/Delete from bubbling to page delete when editing
    e.stopPropagation();
  };

  const style = obj.style || {};
  const cssStyle = {
    position: "absolute",
    left: (obj.x || 0) * scale,
    top: (obj.y || 0) * scale,
    width: (obj.width || 100) * scale,
    height: (obj.height || 40) * scale,
    transform: obj.rotation ? `rotate(${obj.rotation}deg)` : undefined,
    fontFamily: style.fontFamily || "inherit",
    fontSize: (style.fontSize || 11) * scale,
    fontWeight: style.fontWeight || 400,
    color: style.color || "#071c1c",
    lineHeight: style.lineHeight || 1.5,
    textAlign: style.textAlign || "left",
    letterSpacing: style.letterSpacing || "normal",
    textTransform: style.textTransform || "none",
    background: selected && !editing ? "rgba(39,230,209,0.06)" : undefined,
    outline: selected ? "1.5px solid rgba(39,230,209,0.9)" : editing ? "2px solid rgba(39,230,209,0.9)" : "none",
    outlineOffset: 2,
    overflow: editing ? "auto" : "hidden",
    whiteSpace: "pre-wrap",
    userSelect: editing ? "text" : "none",
    // With Text tool active, existing objects must not intercept pointer
    // events so the overlay can capture the drag-to-create gesture.
    pointerEvents: activeTool === "text" && !editing ? "none" : "auto",
    cursor: obj.locked ? "not-allowed" : editing ? "text" : "pointer",
    padding: 2,
  };

  if (obj.type !== "text") {
    return (
      <div style={cssStyle} data-testid={`obj-${obj.id}`}
           onMouseDown={(e) => { e.stopPropagation(); onSelect?.(obj.id); }}>
        [{obj.type}]
      </div>
    );
  }

  return (
    <div
      ref={ref}
      data-testid={`obj-${obj.id}`}
      data-obj-role={obj.role}
      contentEditable={editing && !obj.locked}
      suppressContentEditableWarning
      style={cssStyle}
      onMouseDown={(e) => {
        e.stopPropagation();
        onSelect?.(obj.id);
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (!obj.locked) setEditingId(obj.id);
      }}
      onBlur={editing ? commit : undefined}
      onKeyDown={editing ? onKeyDown : undefined}
    >
      {obj.content || (editing ? "" : (obj.role === "primary-chapter-body" ? "" : "New text"))}
      {obj.locked && (
        <span data-testid={`obj-locked-${obj.id}`}
              className="absolute top-1 right-1 text-[8px] uppercase tracking-[0.2em] text-white/70 bg-black/40 rounded px-1">
          locked
        </span>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- Tool switch */
export function CanvasToolPicker({ activeTool, onChange }) {
  const tools = [
    { key: "select", label: "Select", icon: MousePointer2 },
    { key: "text",   label: "Text",   icon: Type },
  ];
  return (
    <div data-testid="canvas-tool-picker"
         className="fixed top-[76px] left-1/2 -translate-x-1/2 z-30 inline-flex bg-[var(--pl-surface)]/95 backdrop-blur rounded-full border border-[var(--pl-border)] overflow-hidden shadow-lg">
      {tools.map((t) => {
        const I = t.icon;
        const active = activeTool === t.key;
        return (
          <button
            key={t.key}
            data-testid={`canvas-tool-${t.key}`}
            onClick={() => onChange(t.key)}
            className={"h-7 px-3 inline-flex items-center gap-1 text-[11px] " +
              (active ? "bg-[var(--pl-aqua)]/20 text-[var(--pl-aqua)]"
                      : "text-[var(--pl-text)] hover:text-[var(--pl-aqua)]")}>
            <I className="h-3.5 w-3.5" />
            {t.label}
          </button>
        );
      })}
      <div className="h-7 px-2 inline-flex items-center text-[10px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)]"
           title="Drag on a page with the Text tool to create a new decorative text frame">
        <Plus className="h-3 w-3 mr-0.5" /> drag to add
      </div>
    </div>
  );
}
